// src/components/forms/SensorForm.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { Form, Input, Button, Select, App } from 'antd';
import { SensorItem } from '../../types/ProductItems';
import RefOemManager from '../common/RefOemManager';
import ImageUploader from '../common/ImageUploader';
import { toBase64, mapBase64ToUploadList } from '../../utils/formsThumbnails';
import { fetchProductRefsOems } from '../../services/productAPI';

interface SensorFormProps {
  onAdd: (item: SensorItem) => void;
  initialValues?: SensorItem | null;
}
const { Option } = Select;

const nonNegativeRule = (label: string) => ({
  validator(_: any, value: any) {
    if (value === undefined || value === '' || Number(value) >= 0) return Promise.resolve();
    return Promise.reject(new Error(`${label} не может быть отрицательным`));
  },
});

const SensorForm: React.FC<SensorFormProps> = ({ onAdd, initialValues }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [refList, setRefList] = useState<{ [brand: string]: string[] }>({});
  const [oemList, setOemList] = useState<{ [model: string]: string[] }>({});

  const resetForm = useCallback(() => {
    form.resetFields();
    setRefList({});
    setOemList({});
  }, [form]);

  useEffect(() => {
    let mounted = true;
    if (initialValues) {
      const { images = [], ...rest } = initialValues;
      form.setFieldsValue({
        ...rest,
        images: mapBase64ToUploadList(images),
      });
      setRefList(initialValues.references ?? {});
      setOemList(initialValues.oems ?? {});
      const mysqlId = Number(initialValues.id);
      if (Number.isInteger(mysqlId) && mysqlId > 0) {
        const cat = initialValues.categoryName ?? 'crankshaft_sensors';
        fetchProductRefsOems(cat, mysqlId).then(fresh => {
          if (fresh && mounted) {
            setRefList(fresh.references);
            setOemList(fresh.oems);
          }
        });
      }
    } else {
      resetForm();
    }
    return () => { mounted = false; };
  }, [initialValues, form, resetForm]);

  const handleFinish = useCallback(
    async (values: any) => {
      try {
        const imagesField = form.getFieldValue('images') || [];
        const base64Images = await Promise.all(
          imagesField.map(async (file: any) =>
            file?.originFileObj ? await toBase64(file.originFileObj) : file?.url || file?.thumbUrl
          )
        );

        const originalServerUrls = (initialValues?.images ?? [])
          .filter((img: string) => typeof img === 'string' && !img.startsWith('data:'));
        const _deletedImgs = originalServerUrls.filter((img: string) => !base64Images.includes(img));

        const newItem: SensorItem = {
          id: initialValues?.id || crypto.randomUUID(),
          key: initialValues?.key || Date.now().toString(),
          ...values,
          images: base64Images,
          references: refList,
          oems: oemList,
          _deletedImgs,
        } as any;

        await onAdd(newItem);
        message.success(initialValues ? 'Товар обновлён!' : 'Товар добавлен!');
        if (!initialValues) resetForm();
      } catch (error) {
        console.error('Submit error:', error);
        message.error('Ошибка при сохранении. Проверьте данные.');
      }
    },
    [form, refList, oemList, onAdd, initialValues, resetForm]
  );

  return (
    <Form
      form={form}
      layout="vertical"
      name="SensorForm"
      preserve={false}
      onFinish={handleFinish}
    >
      <Form.Item label="Бренд" name="brand" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item label="Номер детали" name="partNumber" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item label="SKU" name="sku">
        <Input maxLength={10} />
      </Form.Item>

      <Form.Item label="Модель" name="model" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item label="Цена" name="price" rules={[{ required: true }, nonNegativeRule('Цена')]}>
        <Input type="number" />
      </Form.Item>

      <Form.Item
        label="Количество (в наличии)"
        name="quantity"
        rules={[{ required: true }, nonNegativeRule('Количество')]}
      >
        <Input type="number" />
      </Form.Item>

      <Form.Item
        label="Тип датчика"
        name="sensorType"
        rules={[{ required: true, message: 'Выберите тип датчика' }]}
      >
        <Select placeholder="Выберите тип датчика">
          <Option value="Коленвал">Коленвал (→ Датчики Коленвал/Распредвал)</Option>
          <Option value="Распредвал">Распредвал (→ Датчики Коленвал/Распредвал)</Option>
          <Option value="Давление масла">Давление масла (→ Датчики Разные)</Option>
          <Option value="Детонация">Детонация (→ Датчики Разные)</Option>
          <Option value="Другое">Другое (→ Датчики Разные)</Option>
        </Select>
      </Form.Item>

      {/* Поле 1: Тип подключения (проводной/беспроводной) */}
      <Form.Item label="Тип подключения" name="connectionType" rules={[{ required: true }]}>
        <Select placeholder="Выберите тип">
          <Option value="wired">Проводной</Option>
          <Option value="wireless">Беспроводной</Option>
        </Select>
      </Form.Item>

      {/* Поле 2: Тип коннектора (Выпадающий список) */}
      <Form.Item 
        label="Тип коннектора" 
        name="connectorType" 
        rules={[{ required: true, message: 'Выберите тип коннектора!' }]}
      >
        <Select placeholder="Выберите форму коннектора">
          {/* Значения (value) написаны так, чтобы совпадать со старой базой данных */}
          <Option value="прямоугольный">Прямоугольный</Option>
          <Option value="круглый">Круглый</Option>
          <Option value="другой">Другой</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Количество контактов"
        name="contactsCount"
        rules={[
          { required: true, message: 'Укажите количество контактов' },
          {
            validator: (_, value) => {
              if (['1','2','3'].includes(String(Number(value)))) return Promise.resolve();
              return Promise.reject(new Error('Допустимые значения: 1, 2 или 3'));
            },
          },
        ]}
      >
        <Input type="number" min={1} max={3} />
      </Form.Item>

      <Form.Item name="original" label="Тип товара" rules={[{ required: true }]}>
        <Select placeholder="Выберите тип">
          <Option value="original">Оригинал</Option>
          <Option value="copy">Копия</Option>
        </Select>
      </Form.Item>

      <ImageUploader />

      <RefOemManager title="REF" data={refList} setData={setRefList} />
      <RefOemManager title="OEM" data={oemList} setData={setOemList} />

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <Button onClick={resetForm}>Очистить форму</Button>
        <Button type="primary" htmlType="submit">
          {initialValues ? 'Сохранить изменения' : 'Добавить продукт'}
        </Button>
      </div>
    </Form>
  );
};
export default SensorForm;